import { useState } from 'react';

import { Button } from '@/components/atoms/NewPcButton';
import SearchIcon from '@/assets/icons/Monitoring/SearchIcon.svg?react';
import PlusIcon from '@/assets/icons/Monitoring/PlusIcon.svg?react';
import CheckIcon from '@/assets/icons/Monitoring/CheckIcon.svg?react';

import { DialogContent } from '@/components/atoms/Dialog';
import { usePost, CPLAT_API_ROUTES } from '@/api';
import { useToast } from '@/hooks/V3/useToast';
import { useAppSelector } from '@/redux/store';

import useCheckMembership from '@/hooks/useCheckMembership';
import DeliveryIcon from '@/assets/icons/Monitoring/IconTagDelivery.svg?react';
import KeywordTable from './KeywordTable';
import { useMonitoringGoods } from './MonitoringContext';
import MembershipDialog from './MembershipDialog';
import NoMembershipDialog from './NoMembershipDialog';

const SubmitMonitoring = ({
  handleSearch,
  searchResult,
  setSearchResult,
  setDialogOpen,
  setUrl,
  url,
  isSearching,
}) => {
  const { showToast } = useToast();
  const user = useAppSelector((state) => state.user);
  const { refetch } = useMonitoringGoods();

  const [relatedList, setRelatedList] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [noMemberDialogOpen, setNoMemberDialogOpen] = useState(false);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);

  const isMember = useCheckMembership();
  // 등록 mutation
  const { mutate: addMonitoringGoods, isPending: isAdding } = usePost(
    CPLAT_API_ROUTES.setUserMonitoringGoods,
  );

  // 등록 버튼 클릭 핸들러 (react-query mutation)
  const handleRegister = () => {
    if (!searchResult) return;
    addMonitoringGoods(
      {
        cplatToken: user.CplatToken,
        StoreName: searchResult.StoreName,
        GoodsNo: searchResult.GoodsNo,
        GoodsImageUrl: searchResult.GoodsImageUrl,
        GoodsName: searchResult.GoodsName,
        CategoryFullName: searchResult.CategoryFullName,
        UseYn: 'Y',
        RelatedKeywordList: relatedList,
        GoodsUrl: searchResult.GoodsUrl,
        GoodsPrice: searchResult.GoodsPrice,
        relatedKeywordRankInfo: searchResult.RelatedKeywordRankingList?.[0],
        ShippingFee: searchResult.ShippingFee,
        GoodsRegisterDate: searchResult.GoodsRegisterDate,
        NoticeYn: 'N',
        IsMyStoreGoods: searchResult.IsMyStoreGoods,
        MallSeq: searchResult.MallSeq,
        InsertYn: 'Y',
      },
      {
        onSuccess: () => {
          showToast('success', '모니터링 상품이 추가되었습니다!');
          setDialogOpen(false);
          setUrl('');
          setSearchResult(null);
          setRelatedList([]);
          refetch(); // 모니터링 상품 목록 새로고침
        },
        onError: () => {
          showToast('warning', '상품 등록에 실패했습니다.');
        },
      },
    );
  };
  const MAX_KEYWORDS = isMember ? 10 : 2;

  // 키워드 토글
  const handleToggleKeyword = (keyword: string) => {
    if (relatedList.includes(keyword)) {
      setRelatedList(relatedList.filter((k) => k !== keyword));
    } else if (relatedList.length < MAX_KEYWORDS) {
      setRelatedList([...relatedList, keyword]);
    } else {
      // showToast('warning', `최대 ${MAX_KEYWORDS}개까지 선택할 수 있습니다.`);
      if (!isMember) {
        setNoMemberDialogOpen(true);
      } else {
        setMemberDialogOpen(true);
      }
    }
  };

  // 키워드 직접 입력 추가
  const handleAddKeyword = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    if (relatedList.includes(trimmed)) {
      showToast('warning', '이미 추가된 키워드입니다.');
      return;
    }
    if (relatedList.length >= MAX_KEYWORDS) {
      showToast('warning', `최대 ${MAX_KEYWORDS}개까지 선택할 수 있습니다.`);
      return;
    }
    setRelatedList([...relatedList, trimmed]);
    setInput('');
  };

  // 키워드 삭제
  const handleRemoveKeyword = (keyword: string) => {
    setRelatedList(relatedList.filter((k) => k !== keyword));
  };

  const totalKeywordList = Array.from(
    new Set([...(searchResult?.KeywordList || []), ...(searchResult?.SellerTagList || [])]),
  );

  return (
    <DialogContent noCloseBtn className="max-w-[1020px]">
      {!searchResult ? (
        <div>검색 결과가 없습니다.</div>
      ) : (
        <div className="flex flex-col gap-[10px]">
          <div className="flex  rounded-[16px] border border-gr-300 bg-white px-[12px] py-[10px] focus-within:border-point-400">
            <input
              type="text"
              placeholder="모니터링할 상품의 Url을 입력해주세요"
              className="h-full flex-1 pr-[10px] text-gr-800 Body5M14 focus:outline-none"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              disabled={isSearching}
            />
            <Button
              className="h-[34px] min-w-[67px] gap-[6px] px-[10px] py-[6px] Body5M14"
              onClick={handleSearch}
              disabled={isSearching}
              isLoading={isSearching}
            >
              <SearchIcon />
              검색
            </Button>
          </div>

          {/* 2. 상품 정보 */}
          <div className="flex  gap-3">
            <img
              src={searchResult.GoodsImageUrl}
              alt={searchResult.GoodsName}
              className="h-[120px] w-[120px] rounded-[8px] border object-cover"
            />
            <div className="flex flex-col gap-[10px]">
              <div className="flex gap-[20px]">
                <div className="flex flex-1 flex-col gap-[10px]">
                  <div className="flex flex-col gap-[6px]">
                    <div className="text-gr-800 Body1S16">{searchResult.GoodsName}</div>
                    <div className="flex flex-col gap-0.5 text-gr-600 Caption1M12">
                      <div className="">{searchResult.CategoryFullName}</div>
                      <div>제품등록일 : {formatDate(searchResult.GoodsRegisterDate)}</div>
                    </div>
                  </div>
                  <div className="flex min-w-[100px] items-center gap-3">
                    <div className="text-gr-800 Body1S16">
                      {searchResult.GoodsPrice?.toLocaleString()}원
                    </div>
                    <div className="flex items-center gap-[8px]">
                      <div className="flex h-[20px] w-[20px] rounded-full bg-[#F3F3F4] p-[3px]">
                        <DeliveryIcon />
                      </div>
                      <div className="text-gr-800 Body5M14">
                        {searchResult.ShippingFee?.toLocaleString()}원
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex  h-full w-[440px] justify-between gap-[20px] rounded-[10px] bg-gr-50 px-[40px] py-[20px]">
                  <div className="flex flex-col items-center justify-center gap-1">
                    <div className="Body6M14 text-gr-500">6개월 판매량</div>
                    <div className="Title2B20 text-gr-800">
                      {searchResult.SalesCountFor6Month?.toLocaleString()}
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-center gap-1">
                    <div className="Body6M14 text-gr-500">리뷰 수</div>
                    <div className="Title2B20 text-gr-800">
                      {searchResult.ReviewCount?.toLocaleString()}
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-center gap-1">
                    <div className="Body6M14 text-gr-500">찜 수</div>
                    <div className="Title2B20 text-gr-800">
                      {searchResult.KeepCount?.toLocaleString()}
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-center gap-1">
                    <div className="Body6M14 text-gr-500">평점</div>
                    <div className="Title2B20 text-gr-800">
                      {searchResult.ReviewScore?.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 3. 키워드 선택 */}
          <div className="flex h-[350px] gap-[20px]">
            <div className="flex max-w-[360px] flex-col gap-[20px] rounded-[16px] border border-gr-200 p-4">
              <div className="text-gr-800 Body3S15">
                순위를 추적할 키워드를 입력하거나 선택하세요
              </div>
              <div className="flex  rounded-[16px] border border-gr-300 bg-white px-[12px] py-[10px] focus-within:border-point-400">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="키워드 입력"
                  className="h-full flex-1 pl-[4px] pr-[10px] text-gr-800 Body5M14 focus:outline-none"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddKeyword()}
                  disabled={relatedList.length >= MAX_KEYWORDS}
                />
                <Button
                  variant="secondary"
                  className="gap-[6px] px-[10px] py-[6px] Body5M14"
                  onClick={handleAddKeyword}
                  disabled={relatedList.length >= MAX_KEYWORDS || !input.trim() || isSearching}
                >
                  추가
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {searchResult.KeywordList?.map((keyword: string) => (
                  <button
                    key={keyword}
                    type="button"
                    onClick={() => handleToggleKeyword(keyword)}
                    className={`Body6M14 flex items-center rounded-[19px] border px-[10px] py-1 text-sm text-gr-700 transition-colors
        ${relatedList.includes(keyword) ? ' border-green-400 ' : 'border-gr-200 '}
      `}
                  >
                    {relatedList.includes(keyword) ? (
                      <CheckIcon className="mr-1 h-4 w-4 text-point-500" />
                    ) : (
                      <PlusIcon className="mr-1 h-4 w-4 text-gr-400" />
                    )}
                    {keyword}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-1 flex-col rounded-[16px] border border-gr-200 p-4">
              <div className=" flex gap-[10px] ">
                <div className="text-gr-800 Body3S15">추가한 키워드예요</div>
                <div className="Body4B14">
                  <span className=" text-point-500">{relatedList.length}</span>
                  <span className="text-gr-500"> / {MAX_KEYWORDS}</span>
                </div>
              </div>
              <KeywordTable relatedList={relatedList} onRemove={handleRemoveKeyword} />
            </div>
          </div>
          {/* 5. 하단 버튼 */}
          <div className=" flex justify-center gap-[12px]">
            <Button
              variant="secondary"
              className="max-w-[300px] flex-1 Body5M14"
              onClick={() => {
                setDialogOpen(false);
                setUrl('');
                setSearchResult(null);
                setRelatedList([]);
              }}
            >
              삭제
            </Button>
            <Button
              variant={'solid'}
              className="max-w-[300px] flex-1 Body5M14"
              onClick={handleRegister}
              disabled={isAdding || relatedList.length === 0}
              isLoading={isAdding}
            >
              상품 모니터링
            </Button>
          </div>
        </div>
      )}
      <MembershipDialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen} />
      <NoMembershipDialog open={noMemberDialogOpen} onOpenChange={setNoMemberDialogOpen} />
    </DialogContent>
  );
};

export default SubmitMonitoring;

function formatDate(dateStr: any) {
  if (!dateStr) return '-';
  // "2025-06-16T15:38:18" → "2025.06.16"
  return dateStr.slice(0, 10).replace(/-/g, '.');
}
